<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useAdmin, type UploadResultado } from '@/composables/useAdmin';

const props = defineProps<{
  apiBaseUrl: string;
  usuarioTipoID: number;
  revisorUsuarioID: number;
}>();

const admin = useAdmin({
  apiBaseUrl: props.apiBaseUrl,
  usuarioTipoID: props.usuarioTipoID,
  revisorUsuarioID: props.revisorUsuarioID
});

// Estado do upload de documento
const arquivoSelecionado = ref<File | null>(null);
const uploadEnviando = ref(false);
const uploadResultado = ref<UploadResultado | null>(null);
const uploadErro = ref<string | null>(null);
const dragSobre = ref(false);

function selecionarArquivo(e: Event) {
  const input = e.target as HTMLInputElement;
  if (input.files && input.files.length > 0) {
    arquivoSelecionado.value = input.files[0]!;
    uploadResultado.value = null;
    uploadErro.value = null;
  }
}

function ondrop(e: DragEvent) {
  e.preventDefault();
  dragSobre.value = false;
  if (e.dataTransfer && e.dataTransfer.files.length > 0) {
    arquivoSelecionado.value = e.dataTransfer.files[0]!;
    uploadResultado.value = null;
    uploadErro.value = null;
  }
}

async function enviarUpload() {
  if (!arquivoSelecionado.value) return;
  uploadEnviando.value = true;
  uploadResultado.value = null;
  uploadErro.value = null;
  try {
    const r = await admin.uploadDocumento(arquivoSelecionado.value);
    uploadResultado.value = r;
    if (!r.erro) {
      arquivoSelecionado.value = null;
    }
  } catch (e) {
    uploadErro.value = e instanceof Error ? e.message : 'Falha no upload';
  } finally {
    uploadEnviando.value = false;
  }
}

function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

onMounted(() => admin.carregar());
watch(admin.filtroStatus, () => admin.carregar());

const obsRevisaoPorId = ref<Record<number, string>>({});
const tituloChunkPorId = ref<Record<number, string>>({});

// Estado de edição inline por correção
const editandoId = ref<number | null>(null);
const textoEditadoPorId = ref<Record<number, string>>({});
const salvandoEdicaoPorId = ref<Record<number, boolean>>({});

function abrirEdicao(id: number, textoAtual: string) {
  editandoId.value = id;
  textoEditadoPorId.value[id] = textoAtual;
}

function cancelarEdicao() {
  editandoId.value = null;
}

async function salvarEdicao(id: number) {
  const novo = (textoEditadoPorId.value[id] ?? '').trim();
  if (!novo) {
    alert('Texto não pode ficar vazio.');
    return;
  }
  salvandoEdicaoPorId.value[id] = true;
  try {
    await admin.editar(id, novo, obsRevisaoPorId.value[id]);
    editandoId.value = null;
  } catch (e) {
    alert(e instanceof Error ? e.message : 'Falha ao salvar edição.');
  } finally {
    salvandoEdicaoPorId.value[id] = false;
  }
}

async function aprovar(id: number) {
  try {
    await admin.aprovar(id, tituloChunkPorId.value[id], obsRevisaoPorId.value[id]);
  } catch (e) {
    alert(e instanceof Error ? e.message : 'Falha ao aprovar.');
  }
}

async function rejeitar(id: number) {
  if (!confirm('Rejeitar essa correção? Ela não vira chunk.')) return;
  try {
    await admin.rejeitar(id, obsRevisaoPorId.value[id]);
  } catch (e) {
    alert(e instanceof Error ? e.message : 'Falha ao rejeitar.');
  }
}

function formatarData(iso: string | null | undefined) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso; }
}
</script>

<template>
  <div class="ad-root">
    <header class="ad-header">
      <h1>Painel — Correções da IA</h1>
      <div class="ad-info">
        <span>Usuário revisor: <strong>{{ revisorUsuarioID }}</strong></span>
        <span>TipoID: <strong>{{ usuarioTipoID }}</strong></span>
      </div>
    </header>

    <!-- ============================== -->
    <!-- Seção: Upload de documento     -->
    <!-- ============================== -->
    <section class="ad-upload">
      <h2>📤 Adicionar documento</h2>
      <p class="ad-upload-help">
        Faz upload pra pasta canônica do Drive + indexa automaticamente. Aceita:
        <strong>.docx</strong> e <strong>.md</strong> (extração + chunking, conteúdo entra no chat),
        <strong>.pdf</strong> (apenas upload — converter pra Doc/docx no Drive pra indexar).
        Arquivos <strong>.md</strong> são tratados como fonte interna: contribuem pros chunks mas não aparecem como manual pra download.
        Limite 20 MB.
      </p>

      <div
        :class="['ad-upload-drop', dragSobre ? 'ativo' : '']"
        @dragover.prevent="dragSobre = true"
        @dragleave.prevent="dragSobre = false"
        @drop="ondrop">
        <input
          type="file"
          accept=".docx,.pdf,.md,.markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,text/markdown,text/plain"
          @change="selecionarArquivo"
          :disabled="uploadEnviando"
          class="ad-upload-input" />
        <div class="ad-upload-msg">
          <span v-if="!arquivoSelecionado">Arraste o arquivo aqui ou clique para escolher</span>
          <span v-else>
            <strong>{{ arquivoSelecionado.name }}</strong>
            ({{ formatarTamanho(arquivoSelecionado.size) }})
          </span>
        </div>
      </div>

      <div class="ad-upload-acoes">
        <button
          v-if="arquivoSelecionado"
          class="ad-btn ad-btn-aprovar"
          :disabled="uploadEnviando"
          @click="enviarUpload">
          {{ uploadEnviando ? 'Enviando + indexando...' : '🚀 Enviar e indexar' }}
        </button>
        <button
          v-if="arquivoSelecionado && !uploadEnviando"
          class="ad-btn ad-btn-cancelar"
          @click="arquivoSelecionado = null">
          Cancelar
        </button>
      </div>

      <div v-if="uploadResultado" class="ad-upload-resultado">
        <strong>{{ uploadResultado.erro ? '⚠️ Atenção:' : '✓ Sucesso!' }}</strong>
        <div>Arquivo: <code>{{ uploadResultado.nomeArquivo }}</code> (Drive ID: <code>{{ uploadResultado.fileID }}</code>)</div>
        <div v-if="uploadResultado.chunksGerados > 0">
          <strong>{{ uploadResultado.chunksGerados }} chunks</strong> gerados em IAConhecimento
          <span v-if="uploadResultado.usouFallbackOpenXml"> (extração via fallback OpenXml)</span>
        </div>
        <div v-if="uploadResultado.erro" class="ad-upload-erro-detail">{{ uploadResultado.erro }}</div>
      </div>
      <div v-if="uploadErro" class="ad-erro" style="margin-top: 12px">{{ uploadErro }}</div>
    </section>

    <section class="ad-toolbar">
      <label>
        Status:
        <select v-model="admin.filtroStatus.value">
          <option value="pendente">Pendentes</option>
          <option value="aprovada">Aprovadas</option>
          <option value="rejeitada">Rejeitadas</option>
          <option value="todos">Todos</option>
        </select>
      </label>
      <button @click="admin.carregar()" :disabled="admin.carregando.value">
        {{ admin.carregando.value ? 'Carregando...' : 'Atualizar' }}
      </button>
    </section>

    <div v-if="admin.erro.value" class="ad-erro">⚠️ {{ admin.erro.value }}</div>

    <div v-if="admin.correcoes.value.length === 0 && !admin.carregando.value" class="ad-vazio">
      Nenhuma correção para o filtro selecionado.
    </div>

    <article v-for="c in admin.correcoes.value" :key="c.id" class="ad-card">
      <header class="ad-card-header">
        <span class="ad-id">#{{ c.id }}</span>
        <span :class="['ad-status', `ad-status-${c.status}`]">{{ c.status.toUpperCase() }}</span>
        <span class="ad-meta">por <strong>{{ c.usuarioNome ?? c.usuarioID }}</strong> em {{ formatarData(c.criadoEm) }}</span>
        <span class="ad-meta">base: {{ c.baseCliente }}</span>
        <span v-if="c.controllerContexto" class="ad-meta">contexto: {{ c.controllerContexto }}{{ c.acaoContexto ? '/' + c.acaoContexto : '' }}</span>
      </header>

      <section class="ad-bloco">
        <div class="ad-label">Pergunta original</div>
        <div class="ad-texto">{{ c.perguntaOriginal }}</div>
      </section>

      <section v-if="c.respostaOriginal" class="ad-bloco">
        <div class="ad-label">Resposta da IA (errada)</div>
        <div class="ad-texto ad-texto-erro">{{ c.respostaOriginal }}</div>
      </section>

      <section class="ad-bloco">
        <div class="ad-label">
          Correção sugerida
          <button
            v-if="editandoId !== c.id"
            class="ad-btn-editar"
            @click="abrirEdicao(c.id, c.correcaoSugerida)">
            ✏️ Editar
          </button>
        </div>
        <div v-if="editandoId !== c.id" class="ad-texto ad-texto-correcao">{{ c.correcaoSugerida }}</div>
        <div v-else class="ad-edicao">
          <textarea
            v-model="textoEditadoPorId[c.id]"
            rows="10"
            class="ad-textarea-edicao"
            :disabled="salvandoEdicaoPorId[c.id]"
            placeholder="Edite a correção..."></textarea>
          <div class="ad-edicao-acoes">
            <button class="ad-btn ad-btn-cancelar" :disabled="salvandoEdicaoPorId[c.id]" @click="cancelarEdicao">Cancelar</button>
            <button
              class="ad-btn ad-btn-salvar"
              :disabled="salvandoEdicaoPorId[c.id] || !(textoEditadoPorId[c.id] ?? '').trim()"
              @click="salvarEdicao(c.id)">
              {{ salvandoEdicaoPorId[c.id] ? 'Salvando...' : (c.status === 'aprovada' ? 'Salvar (atualiza chunk)' : 'Salvar') }}
            </button>
          </div>
        </div>
      </section>

      <section v-if="c.status === 'pendente'" class="ad-acoes">
        <input
          v-model="tituloChunkPorId[c.id]"
          placeholder="Título do chunk (opcional — auto-gerado se vazio)"
          class="ad-input" />
        <input
          v-model="obsRevisaoPorId[c.id]"
          placeholder="Observação da revisão (opcional)"
          class="ad-input" />
        <button class="ad-btn ad-btn-aprovar" @click="aprovar(c.id)">✓ Aprovar (cria chunk)</button>
        <button class="ad-btn ad-btn-rejeitar" @click="rejeitar(c.id)">✕ Rejeitar</button>
      </section>

      <section v-else class="ad-revisado">
        <span>Revisado em {{ formatarData(c.revisadoEm) }} por usuário {{ c.revisadoPorUsuarioID }}</span>
        <span v-if="c.chunkGeradoID">Chunk gerado: <strong>#{{ c.chunkGeradoID }}</strong></span>
        <span v-if="c.observacaoRevisao">Obs: {{ c.observacaoRevisao }}</span>
      </section>
    </article>
  </div>
</template>

<style scoped>
.ad-root { max-width: 980px; margin: 0 auto; padding: 24px 20px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111827; }

.ad-header { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 12px; border-bottom: 2px solid #e5e7eb; padding-bottom: 14px; margin-bottom: 20px; }
.ad-header h1 { margin: 0; font-size: 22px; }
.ad-info { display: flex; gap: 16px; font-size: 13px; color: #6b7280; }

.ad-toolbar { display: flex; gap: 12px; align-items: center; margin-bottom: 16px; }
.ad-toolbar label { font-size: 13px; color: #374151; }
.ad-toolbar select { margin-left: 6px; padding: 4px 8px; border: 1px solid #d1d5db; border-radius: 4px; }
.ad-toolbar button { padding: 6px 12px; background: #4f46e5; color: white; border: none; border-radius: 4px; cursor: pointer; }
.ad-toolbar button:disabled { opacity: 0.5; cursor: not-allowed; }

.ad-erro { background: #fee2e2; color: #b91c1c; padding: 12px; border-radius: 6px; margin-bottom: 16px; }
.ad-vazio { text-align: center; color: #6b7280; padding: 40px; font-size: 14px; }

.ad-card { background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
.ad-card-header { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; font-size: 12px; color: #6b7280; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #f3f4f6; }
.ad-id { font-weight: 700; color: #111827; }
.ad-status { padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 11px; }
.ad-status-pendente { background: #fef3c7; color: #92400e; }
.ad-status-aprovada { background: #d1fae5; color: #047857; }
.ad-status-rejeitada { background: #fee2e2; color: #b91c1c; }
.ad-meta { font-size: 12px; }

.ad-bloco { margin-bottom: 10px; }
.ad-label { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.ad-texto { background: #f9fafb; padding: 10px 12px; border-radius: 6px; font-size: 13px; line-height: 1.5; white-space: pre-wrap; }
.ad-texto-erro { background: #fef2f2; border-left: 3px solid #dc2626; }
.ad-texto-correcao { background: #f0fdf4; border-left: 3px solid #16a34a; }

.ad-acoes { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; padding-top: 10px; border-top: 1px solid #f3f4f6; }
.ad-input { flex: 1; min-width: 200px; padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 13px; }
.ad-btn { padding: 6px 14px; border: none; border-radius: 4px; cursor: pointer; font-weight: 600; font-size: 13px; }
.ad-btn-aprovar { background: #16a34a; color: white; }
.ad-btn-aprovar:hover { background: #15803d; }
.ad-btn-rejeitar { background: #dc2626; color: white; }
.ad-btn-rejeitar:hover { background: #b91c1c; }

.ad-revisado { margin-top: 8px; padding-top: 8px; border-top: 1px solid #f3f4f6; display: flex; flex-wrap: wrap; gap: 16px; font-size: 12px; color: #6b7280; }

.ad-label { display: flex; align-items: center; gap: 8px; }
.ad-btn-editar { background: transparent; border: 1px solid #d1d5db; color: #374151; font-size: 11px; padding: 2px 8px; border-radius: 4px; cursor: pointer; font-weight: 400; text-transform: none; letter-spacing: normal; }
.ad-btn-editar:hover { background: #f9fafb; border-color: #9ca3af; }

.ad-edicao { display: flex; flex-direction: column; gap: 8px; }
.ad-textarea-edicao { width: 100%; resize: vertical; min-height: 120px; border: 2px solid #4f46e5; border-radius: 6px; padding: 10px 12px; font-family: inherit; font-size: 13px; line-height: 1.5; box-sizing: border-box; outline: none; }
.ad-textarea-edicao:focus { border-color: #4338ca; box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1); }
.ad-edicao-acoes { display: flex; gap: 8px; justify-content: flex-end; }
.ad-btn-salvar { background: #4f46e5; color: white; border: none; padding: 6px 14px; border-radius: 4px; cursor: pointer; font-weight: 600; font-size: 13px; }
.ad-btn-salvar:hover { background: #4338ca; }
.ad-btn-salvar:disabled { opacity: 0.5; cursor: not-allowed; }
.ad-btn-cancelar { background: white; border: 1px solid #d1d5db; color: #374151; padding: 6px 14px; border-radius: 4px; cursor: pointer; font-size: 13px; }

.ad-upload { background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 24px; }
.ad-upload h2 { margin: 0 0 8px 0; font-size: 16px; color: #111827; }
.ad-upload-help { font-size: 13px; color: #6b7280; margin: 0 0 14px 0; line-height: 1.4; }
.ad-upload-drop { position: relative; border: 2px dashed #cbd5e1; border-radius: 8px; padding: 30px; text-align: center; transition: border-color 0.2s, background 0.2s; cursor: pointer; }
.ad-upload-drop.ativo { border-color: #4f46e5; background: #eef2ff; }
.ad-upload-input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.ad-upload-msg { color: #6b7280; font-size: 14px; pointer-events: none; }
.ad-upload-msg strong { color: #111827; }
.ad-upload-acoes { display: flex; gap: 8px; margin-top: 12px; }
.ad-upload-resultado { margin-top: 14px; padding: 12px; background: #f0fdf4; border-left: 3px solid #16a34a; border-radius: 6px; font-size: 13px; line-height: 1.5; }
.ad-upload-resultado code { background: white; padding: 1px 4px; border-radius: 3px; font-size: 12px; }
.ad-upload-erro-detail { margin-top: 6px; padding: 6px; background: #fef3c7; color: #92400e; border-radius: 4px; font-size: 12px; }
</style>
